import { useRef, useState } from "react";
import Reveal from "./Reveal";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { specMove } from "./glass";
import { Plus, MoveUpRight, Minus } from "lucide-react";

const PROFILES = [
  { id: "creators", label: "Creators with an audience who want their content to perform better." },
  { id: "founders", label: "Founders and businesses with something worth putting in front of more people." },
  { id: "experts", label: "People who already have a successful business or income source and want someone they can trust to take care of their social media, from strategy to execution, and focus on getting real results." },
];

const WANTS = [
  "Be hands off with social media and get better results.",
  "Improve the quality of their content.",
  "Save 90% of the time spent on content production.",
  "Fast track their growth.",
];

const REST_ROTATION = ["-3deg", "0deg", "3deg"];

const CornerMarks = ({ n }) => (
  <>
    <div className="absolute left-6 top-6 flex flex-col items-center gap-1.5">
      <span className="font-display text-sm font-bold text-[#1d1d1f]">{n}</span>
      <img src="/vontrail-logo.png" alt="" aria-hidden="true" className="h-5 w-5 rounded-[5px] object-cover" />
    </div>
    <div className="absolute bottom-6 right-6 flex rotate-180 flex-col items-center gap-1.5">
      <span className="font-display text-sm font-bold text-[#1d1d1f]">{n}</span>
      <img src="/vontrail-logo.png" alt="" aria-hidden="true" className="h-5 w-5 rounded-[5px] object-cover" />
    </div>
  </>
);

const PokerCard = ({ p, i, onExpand }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 90, damping: 13 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 90, damping: 13 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      className="h-full transition-transform duration-500 hover:rotate-0"
      style={{ rotate: REST_ROTATION[i] }}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        className="h-full"
        style={{ perspective: 900 }}
      >
        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
          onHoverStart={() => onExpand(p, i)}
          whileTap={{ scale: 0.97 }}
          transition={{ type: "spring", stiffness: 220, damping: 17 }}
          className="relative flex h-full min-h-[340px] flex-col items-center justify-center rounded-[1.5rem] bg-white p-7 text-center shadow-[0_30px_70px_rgba(0,0,0,0.55)] cursor-default"
          data-testid={`who-row-${p.id}`}
        >
          <CornerMarks n={String(i + 1).padStart(2, "0")} />
          <h3
            className="font-display text-xl sm:text-[1.35rem] font-semibold tracking-[-0.02em] leading-snug text-[#1d1d1f] px-2"
            style={{ transform: "translateZ(26px)" }}
          >
            {p.label}
          </h3>
        </motion.div>
      </div>
    </div>
  );
};

const OverlayVectors = () => (
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    <motion.div
      className="absolute left-[12%] top-[18%] h-28 w-28 rounded-full border border-white/15"
      animate={{ y: [0, -16, 0], rotate: [0, 20, 0] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute right-[14%] top-[24%] text-white/15"
      animate={{ y: [0, 14, 0], rotate: [0, -15, 0] }}
      transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
    >
      <Plus className="h-10 w-10" strokeWidth={1} />
    </motion.div>
    <motion.div
      className="absolute left-[20%] bottom-[18%] text-white/15"
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
    >
      <MoveUpRight className="h-9 w-9" strokeWidth={1} />
    </motion.div>
    <motion.div
      className="absolute right-[22%] bottom-[22%] text-white/15"
      animate={{ x: [0, 12, 0] }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
    >
      <Minus className="h-10 w-10" strokeWidth={1} />
    </motion.div>
    <svg
      className="absolute left-1/2 top-1/2 w-[900px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-25"
      viewBox="0 0 900 260"
      fill="none"
    >
      <motion.path
        d="M20 230 C 160 220, 280 190, 400 170 S 580 110, 700 80 S 850 30, 880 22"
        stroke="#22c55e"
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.2, ease: "easeInOut" }}
      />
    </svg>
  </div>
);

export default function WhoWeWorkWith() {
  const [active, setActive] = useState(null);

  return (
    <section id="who" className="bg-[#0a0a0a] py-28 sm:py-40" data-testid="chapter-05-who-we-work-with">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <h2 className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.06] text-white">
            Who we work with
          </h2>
          <div className="mt-6 max-w-xl text-[#a1a1a6] text-lg sm:text-xl leading-relaxed">
            <p className="text-white font-medium">For people who want to:</p>
            <ul className="mt-4 space-y-2">
              {WANTS.map((w) => (
                <li
                  key={w}
                  className="group flex items-center gap-3.5 -mx-3 rounded-2xl px-3 py-2.5 cursor-default transition-all duration-300 hover:translate-x-2 hover:bg-white/[0.05]"
                >
                  <span
                    className="h-2 w-2 shrink-0 rotate-45 rounded-[2px] bg-white/50 transition-all duration-300 group-hover:rotate-[225deg] group-hover:scale-110 group-hover:bg-[#22c55e]"
                    aria-hidden="true"
                  />
                  <span className="transition-colors duration-300 group-hover:text-white">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-16 sm:mt-20 grid gap-8 md:grid-cols-3 md:items-stretch">
        {PROFILES.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1} y={28} className="h-full">
            <PokerCard p={p} i={i} onExpand={(profile, idx) => setActive({ ...profile, n: String(idx + 1).padStart(2, "0") })} />
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-xl px-6"
            data-testid="who-card-overlay"
          >
            <OverlayVectors />
            <motion.div
              initial={{ scale: 0.55, y: 80, rotate: -5, opacity: 0 }}
              animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
              exit={{ scale: 0.7, y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 18 }}
              onMouseLeave={() => setActive(null)}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl rounded-[2rem] bg-white px-10 py-24 sm:px-16 text-center shadow-[0_60px_140px_rgba(0,0,0,0.8)]"
              data-testid="who-card-fullscreen"
            >
              <CornerMarks n={active.n} />
              <span className="eyebrow text-[#86868b]">Who we work with — {active.n}</span>
              <h3 className="mt-8 font-display text-2xl sm:text-4xl font-semibold tracking-[-0.02em] leading-snug text-[#1d1d1f]">
                {active.label}
              </h3>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
